/**
 * The server-only `quest_point` marker (ADR-139 decision 3.4): a place a `reach` objective names, the cells a
 * character's feet must enter, and a label for the minimap arrow. Pure: the editor's Issues list and the compile read
 * it the same way; whether a quest names a place that exists is the compile's check.
 */

import type { EntityInstance, JsonSchema, MapDocument, ValidationIssue } from "daedalus-core";
import { QUEST_POINT } from "../editor/type-names.ts";
import { PLACE_ID_PATTERN } from "./objectives.ts";

/**
 * The most code points a quest point's label holds.
 */
export const MAX_QUEST_POINT_LABEL_CHARS = 48;

/**
 * The marker's fields as the editor's inspector draws them.
 */
export const QUEST_POINT_SCHEMA: JsonSchema = {
  type: "object",
  properties: {
    place: { type: "string", title: "Place" },
    label: { type: "string", title: "Label", default: "", maxLength: MAX_QUEST_POINT_LABEL_CHARS },
    cells: { type: "cells", title: "Cells" },
  },
  required: ["place", "cells"],
};

/**
 * The sentences, after the marker's name, for each bad field of one quest point.
 *
 * @param fields The marker's fields.
 * @returns The errors: a bad place, a label that is not text or is too long, no cells.
 */
function fieldErrors(fields: EntityInstance["fields"]): string[] {
  const errors: string[] = [];
  const place = fields["place"];
  if (typeof place !== "string" || place === "") {
    errors.push("names no place; give it one");
  } else if (!PLACE_ID_PATTERN.test(place)) {
    errors.push(`names the place ${place}; use lower-case letters, digits and _`);
  }
  const label = fields["label"];
  if (label !== undefined && label !== null && typeof label !== "string") {
    errors.push("has a label that is not text");
  } else if (typeof label === "string" && [...label].length > MAX_QUEST_POINT_LABEL_CHARS) {
    errors.push(`has a label longer than ${MAX_QUEST_POINT_LABEL_CHARS} characters`);
  }
  const cells = fields["cells"];
  if (!Array.isArray(cells) || cells.length === 0) {
    errors.push("has no cells; paint at least one");
  }
  return errors;
}

/**
 * The Issues a map's quest points raise in the editor and at a compile: one per bad field, and one for each marker
 * naming a place an earlier marker of the map already names.
 *
 * @param map The map.
 * @returns The issues, each naming the marker.
 */
export function questPointIssues(map: MapDocument): ValidationIssue[] {
  const issues: ValidationIssue[] = [];
  const owners = new Map<string, string>();
  for (const layer of map.layers) {
    if (layer.kind !== "entity") {
      continue;
    }
    for (const entity of layer.entities) {
      if (entity.type !== QUEST_POINT) {
        continue;
      }
      /**
       * Records one issue against this marker.
       * @param message The sentence after its name.
       */
      const issue = (message: string): void => {
        issues.push({ mapId: map.id, kind: "quest_point.field", layerId: layer.id, entityId: entity.id, message: `quest point ${entity.id} ${message}` });
      };
      fieldErrors(entity.fields).forEach(issue);
      const place = entity.fields["place"];
      if (typeof place === "string" && PLACE_ID_PATTERN.test(place)) {
        const owner = owners.get(place);
        if (owner === undefined) {
          owners.set(place, entity.id);
        } else {
          issue(`names the place ${place}, which quest point ${owner} already names on this map`);
        }
      }
    }
  }
  return issues;
}
